import { FormControl, MenuItem, Select } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useCombinedColorSchemes from '../../../../hooks/useCombinedColorSchemes';
import { isColorDark } from './colorUtils';
import { RootState } from '../../../../redux';
import { useSelector } from 'react-redux';

interface ColorSchemeSelectorProps {
  selectedMenu?: string;
  currentColorScheme?: string;
  resetSelection: (value: string) => void;
  from?: string;
  color?: string;
}

const ColorSchemeSelector = ({
  selectedMenu,
  currentColorScheme,
  resetSelection,
  from,
  color,
}: ColorSchemeSelectorProps) => {
  const combinedThemes = useCombinedColorSchemes();

  const tabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);
  const defaultThemeId = useSelector(
    (state: RootState) => state.tabState.tabs[tabId]?.dashboardState.defaultThemeId,
  );

  const [initialTheme, setInitialTheme] = useState<string | null>(null);

  useEffect(() => {
    if (defaultThemeId && combinedThemes.length > 0) {
      const defTheme = combinedThemes.find(
        (t) => t.id === defaultThemeId || t.themeId === defaultThemeId,
      );
      if (defTheme) {
        setInitialTheme(defTheme.name);
        return;
      }
    }

    // only fallback if no default themeId is set
    if (!defaultThemeId && combinedThemes.length > 0) {
      setInitialTheme(combinedThemes[0].name);
    }
  }, [defaultThemeId, combinedThemes]);

  return (
    <FormControl
      fullWidth
      size="small"
      style={{
        fontSize: '12px',
        borderRadius: '4px',
        width: '100%',
        margin: '0px 4px',
        paddingLeft: from === 'dashboard' ? '4px' : '3px',
        paddingRight: from === 'dashboard' ? '0px' : '5px',
      }}
    >
      <Select
        size="small"
        value={selectedMenu || currentColorScheme || initialTheme || ''}
        onChange={(e) => resetSelection(e.target.value)}
        sx={{
          fontSize: '14px',
          '.MuiSelect-select': {
            padding: '5px 30px 5px 5px !important',
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: 'lightgray',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'lightgray',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2bb9bb',
          },
          '.MuiSelect-icon': {
            color: color || '#2bb9bb', // Or any other color you want
          },
        }}
      >
        {combinedThemes.map((item) => (
          <MenuItem value={item.name} key={item.name} sx={{ padding: '5px 5px' }}>
            <div
              className="custom-option"
              style={{
                backgroundColor: item.background,
                color:
                  ['purplePassion', 'chalk', 'dark', 'halloween'].includes(item.name) ||
                  isColorDark(item.background)
                    ? 'white'
                    : '#3b3b3b',
              }}
            >
              <span className="color-name">{item.name}</span>
              <div className="color-palette" style={{ display: 'flex' }}>
                {item.colors.map((color: string) => (
                  <div
                    className="indi-color"
                    style={{
                      height: '8px',
                      width: '12px',
                      background: color,
                    }}
                    key={`${item.name}_${color}`}
                  />
                ))}
              </div>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ColorSchemeSelector;
